import dayjs from "dayjs";
import { ColorIcon, colors, HStack, VStack } from "goi_common";
import { useRouter } from "next/router";

import { isJejuAddress } from "@/utils/isJejuAddress";

import { useGetObituary } from "../hooks/useGetObituary";
import {
  BugoFormPreviewItem,
  BugoFormPreviewItemPlaceholder,
} from "./BugoFormPreviewSection";
import { BugoSection } from "./BugoSection";

interface BugoFormBinsoPreviewProps {
  readOnly?: boolean;
  obituaryKey?: string;
}

export const BugoFormBinsoPreview = ({
  readOnly,
  obituaryKey,
}: BugoFormBinsoPreviewProps) => {
  const { data } = useGetObituary();

  const binsoValues = [
    {
      key: "빈소",
      value:
        `${data?.parlor?.companyname || ""} ${data?.binso || ""}`.trim() || "",
    },
    {
      key: "입실",
      value: data?.enter_parlor_at
        ? dayjs(data?.enter_parlor_at).format("YYYY년 MM월 DD일 HH시mm분")
        : "",
      hideIfEmpty: false,
    },
    ...(isJejuAddress(data?.parlor?.address) && data?.is_ilpo_public
      ? [{ key: "일포", value: formatIlpo(data?.ilpo_at, data?.ilpo_on) }]
      : []),
    {
      key: "입관",
      value: data?.enter_coffin_at
        ? dayjs(data?.enter_coffin_at).format("YYYY년 MM월 DD일 HH시mm분")
        : "",
    },
    {
      key: "발인",
      value: data?.leave_parlor_at
        ? dayjs(data?.leave_parlor_at).format("YYYY년 MM월 DD일 HH시mm분")
        : "",
    },
    { key: "장지", value: data?.jangji || "" },
  ];

  const router = useRouter();
  const isSomeValidValue = binsoValues.some(({ value }) => value);
  const actionButton = !readOnly ? (
    <HStack
      as="button"
      gap="4px"
      alignItems="center"
      className="body_14_m"
      css={{
        width: "fit-content",
        padding: "4px 12px",
        borderRadius: "20px",
        border: `1px solid ${colors.gray500}`,
      }}
      onClick={() => {
        obituaryKey
          ? router.push(`/bugo/update/binso?obituaryKey=${obituaryKey}`)
          : router.push("/bugo/update/binso/");
      }}
    >
      <ColorIcon name="icon-pencil" size="16px" color={colors.black} />
      {isSomeValidValue ? "정보 수정하기" : "정보 입력하기"}
    </HStack>
  ) : undefined;

  return (
    <BugoSection title="빈소" action={actionButton}>
      {binsoValues && binsoValues.filter(({ value }) => value).length > 0 ? (
        <VStack gap="20px">
          {binsoValues.map(({ key, value, hideIfEmpty }) => {
            if (value) {
              return (
                <BugoFormPreviewItem
                  key={key + value}
                  left={key}
                  right={value}
                />
              );
            } else if (hideIfEmpty) {
              return null;
            } else {
              return (
                <BugoFormPreviewItemPlaceholder
                  key={key + value}
                  left={key}
                  right="미정"
                />
              );
            }
          })}
        </VStack>
      ) : (
        <BugoFormBinsoPreviewPlaceholder />
      )}
    </BugoSection>
  );
};

const BugoFormBinsoPreviewPlaceholder = () => {
  return (
    <VStack gap="20px">
      <BugoFormPreviewItemPlaceholder left="빈소" right="_______ 장례식장" />
      <BugoFormPreviewItemPlaceholder
        left="입관"
        right="______년 ___월 ___일 ___시 ___분"
      />
      <BugoFormPreviewItemPlaceholder
        left="발인"
        right="______년 ___월 ___일 ___시 ___분"
      />
      <BugoFormPreviewItemPlaceholder left="장지" right="_______ 추모공원" />
    </VStack>
  );
};

/** 다른 일시와 같은 표기. 시간 없으면 날짜만, 둘 다 없으면 "" -> 미정 표기 */
const formatIlpo = (ilpoAt?: string | null, ilpoOn?: string | null) => {
  if (ilpoAt) return dayjs(ilpoAt).format("YYYY년 MM월 DD일 HH시mm분");
  return ilpoOn ? dayjs(ilpoOn).format("YYYY년 MM월 DD일") : "";
};
